import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import makeStyles from '@mui/styles/makeStyles';

import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

import { AudienceDetails } from '../modules/audience-details';
import { CategoryDashboard } from '../modules/audience-category-dashboard';
import { AudienceCard } from '../modules/audience-card';

const sortingOptions = [
    {
        label: 'Sort by: Name',
        value: 'name',
        direction: 'asc',
    },
    {
        label: 'Sort by: Newest',
        value: '_created',
        direction: 'desc',
    },
    {
        label: 'Sort by: Last Updated',
        value: 'lastRefreshed',
        direction: 'desc',
    },
];

export const manifoldMetaDataDescriptions = [
    {
        getValue: ({ segment }) => _.get(segment, 'manifoldMetadata.avgIncome', 'N/A') || 'N/A',
        label: 'Average Income',
    },
    {
        getValue: ({ segment }) => _.get(segment, 'manifoldMetadata.homeValue', 'N/A') || 'N/A',
        label: 'Home Value',
    },
    {
        getValue: ({ segment }) =>
            _.get(segment, 'manifoldMetadata.avgMaintainersAge', 'N/A') || 'N/A',
        label: "Average Maintainer's Age",
    },
    {
        getValue: ({ segment }) => _.get(segment, 'manifoldMetadata.householdSize', 'N/A') || 'N/A',
        label: 'Household Size',
    },
];

export const leftSideSegmentDescriptions = [
    {
        getValue: ({ segment }) => _.get(segment, 'manifoldMetadata.residence', 'N/A') || 'N/A',
        label: 'Residence',
        img: '/images/audience-segments/audience-details_residence.png',
    },
    {
        getValue: ({ segment }) => _.get(segment, 'manifoldMetadata.ancestry', 'N/A') || 'N/A',
        label: 'Ancestry',
        img: '/images/audience-segments/audience-details_ancestry.png',
    },
    {
        getValue: ({ segment }) => _.get(segment, 'manifoldMetadata.shopping', 'N/A') || 'N/A',
        label: 'Shopping',
        img: '/images/audience-segments/audience-details_shopping.png',
    },
    {
        getValue: ({ segment }) => _.get(segment, 'manifoldMetadata.education', 'N/A') || 'N/A',
        label: 'Education',
        img: '/images/audience-segments/audience-details_education.png',
    },
];

export const rightSideSegmentDescriptions = [
    {
        value: 'lifestyle',
        getValue: ({ segment }) => _.get(segment, 'manifoldMetadata.lifestyle', 'N/A') || 'N/A',
        label: 'Lifestyle',
        img: '/images/audience-segments/audience-details_lifestyle.png',
    },
    {
        value: 'jobType',
        getValue: ({ segment }) => _.get(segment, 'manifoldMetadata.jobType', 'N/A') || 'N/A',
        label: 'Job Type',
        img: '/images/audience-segments/audience-details_job.png',
    },
    {
        value: 'bigTicket',
        getValue: ({ segment }) => _.get(segment, 'manifoldMetadata.purchaseItems', 'N/A') || 'N/A',
        label: 'Purchasing Big Ticket Items',
        img: '/images/audience-segments/audience-details_lifestyle.png',
    },
];

const useStyles = makeStyles(() => ({
    image: {
        maxWidth: 400,
        width: '100%',
    },
    iconWrapper: {
        display: 'inline-flex',
        height: '112px',
        width: '112px',
        backgroundColor: 'rgba(12, 186, 170, 0.12)',
        borderRadius: '50%',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#098679',
    },
}));

const Layout = ({ audienceSegments, onSearch, activeAudience, organizationId }) => {
    const classes = useStyles();
    const [audiences, setAudiences] = useState(audienceSegments);
    const [segmentInFocus, setSegmentInFocus] = useState(null);
    const [activeSort] = useState('name');

    useEffect(() => {
        setAudiences(audienceSegments);
    }, [audienceSegments]);

    const handleSegmentSelect = segment => {
        setSegmentInFocus(segment);
    };

    const handleClose = () => {
        setSegmentInFocus(null);
    };

    const sortedAudiences = _.orderBy(
        audiences,
        [activeSort],
        [_.find(sortingOptions, option => activeSort === option.value).direction]
    );

    const goToNextAudience = () => {
        const currentAudienceIndex = _.findIndex(
            sortedAudiences,
            audience => audience.id === segmentInFocus.id
        );

        const nextSegment = sortedAudiences[currentAudienceIndex + 1];

        setSegmentInFocus(nextSegment);
    };

    const goToPrevAudience = () => {
        const currentAudienceIndex = _.findIndex(
            sortedAudiences,
            audience => audience.id === segmentInFocus.id
        );

        const nextSegment = sortedAudiences[currentAudienceIndex - 1];

        setSegmentInFocus(nextSegment);
    };

    const linkToAudienceTab = {
        pathname: '/audiences/all',
        query: { audienceTab: 'partnerships' },
        linkBackLabel: 'Partnerships Data',
    };

    return (
        <Box>
            <CategoryDashboard
                audienceSegments={audienceSegments}
                onSearch={onSearch}
                segmentCount={audienceSegments ? audienceSegments.length : 0}
                activeAudience={activeAudience}
                organizationId={organizationId}
                sortingOptions={sortingOptions}
                description={
                    "Combining Pelmorex geolocation data with verified CanaCode insights, these segments identify Canada's consumer landscape into distinct lifestyles at the postal code level."
                }
                title={'Manifold - Canacode'}
                linkToAudienceTab={linkToAudienceTab}
                bannerImagePath={'/images/audience-segments/banners/manifold canacode-banner.png'}
                renderAudienceCard={segment => (
                    <AudienceCard
                        segment={segment}
                        onClick={() => handleSegmentSelect(segment)}
                        active={segment.id === activeAudience}
                        organizationId={organizationId}
                    />
                )}
            />
            <Container>
                {!_.isNil(segmentInFocus) && (
                    <AudienceDetails
                        isOpen={!_.isNil(segmentInFocus)}
                        segment={segmentInFocus}
                        onClose={handleClose}
                        onClickNextAudience={goToNextAudience}
                        onClickPrevAudience={goToPrevAudience}
                        isLastSegment={
                            _.findIndex(sortedAudiences, { id: segmentInFocus.id }) ===
                            sortedAudiences.length - 1
                        }
                        isFirstSegment={
                            _.findIndex(sortedAudiences, { id: segmentInFocus.id }) === 0
                        }
                        segmentDetails={manifoldMetaDataDescriptions}
                        summaryPoints={segmentInFocus.manifoldMetadata.summaryPoints}
                        body={
                            <React.Fragment>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Grid container direction="column" spacing={2}>
                                            {_.map(leftSideSegmentDescriptions, description => (
                                                <Grid
                                                    item
                                                    key={`${segmentInFocus.id}_${
                                                        description.label
                                                    }`}
                                                >
                                                    <Card elevation={0} variant="outlined">
                                                        <CardContent>
                                                            <Grid container spacing={2}>
                                                                <Grid item xs={4}>
                                                                    <img
                                                                        className={classes.image}
                                                                        src={description.img}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={8}>
                                                                    <Typography gutterBottom>
                                                                        {description.label}
                                                                    </Typography>
                                                                    <Typography>
                                                                        {description.getValue({
                                                                            segment: segmentInFocus,
                                                                        })}
                                                                    </Typography>
                                                                </Grid>
                                                            </Grid>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Grid container direction="column" spacing={2}>
                                            {_.map(rightSideSegmentDescriptions, description => (
                                                <Grid
                                                    item
                                                    key={`${segmentInFocus.id}_${
                                                        description.label
                                                    }`}
                                                >
                                                    <Card elevation={0} variant="outlined">
                                                        <CardContent>
                                                            <Grid container spacing={2}>
                                                                <Grid item xs={4}>
                                                                    <img
                                                                        className={classes.image}
                                                                        src={description.img}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={8}>
                                                                    <Typography gutterBottom>
                                                                        {description.label}
                                                                    </Typography>
                                                                    <Typography>
                                                                        {description.getValue({
                                                                            segment: segmentInFocus,
                                                                        })}
                                                                    </Typography>
                                                                </Grid>
                                                            </Grid>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </React.Fragment>
                        }
                    />
                )}
            </Container>
        </Box>
    );
};

export default Layout;
